import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { getMemberGroupsOfMember, removeMemberGroupMembers } from '../../../../api/member-group';
import { errorMessages } from '../../../../utils/methods';
import GroupAddPopover from './GroupAddPopover';
import {
  GroupChipRow,
  GroupChipLabel,
  GroupChipList,
  GroupChip,
  GroupChipRemove,
  GroupChipAddButton,
  GroupChipEmptyText,
} from './MemberGroupModalStyled';

/**
 * 회원 상세에서 이 회원이 속한 그룹을 칩으로 보여주고, 그 자리에서 넣고 뺀다.
 * 담기는 회원목록과 같은 팝오버를 재사용한다(회원 1명짜리 선택으로 넘긴다).
 * branchId 는 화면에서 고른 지점을 그대로 받는다 — 그룹은 지점 스코프라 로그인 지점과 다를 수 있다.
 */
const MemberGroupChips = ({ memberId, memberName, branchId }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);

  const [addAnchor, setAddAnchor] = useState(null);

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['memberGroupsOfMember', memberId, branchId],
    queryFn: () => getMemberGroupsOfMember(user.token, memberId, branchId),
    enabled: !!memberId && !!branchId && !!user?.token,
  });

  const removeMutation = useMutation({
    mutationFn: groupId => removeMemberGroupMembers(user.token, groupId, [memberId]),
    onSuccess: (result, groupId) => {
      const removed = groups.find(group => group.id === groupId);
      toast.success(`${removed?.name || t('그룹')}${t('에서 제외했습니다')}`);
      queryClient.invalidateQueries({ queryKey: ['memberGroupsOfMember', memberId, branchId] });
      // 그룹 관리 모달·회원목록 필터가 같은 데이터를 본다.
      queryClient.invalidateQueries({ queryKey: ['memberGroups', branchId] });
      queryClient.invalidateQueries({ queryKey: ['memberGroupMembers'] });
    },
    onError: error => toast.error(errorMessages(error)),
  });

  if (!branchId) return null;

  return (
    <GroupChipRow>
      <GroupChipLabel>{t('회원 그룹')}</GroupChipLabel>
      <GroupChipList>
        {isLoading ? (
          <CircularProgress size={13} sx={{ color: '#BBB' }} />
        ) : (
          <>
            {groups.length === 0 && <GroupChipEmptyText>{t('소속 그룹 없음')}</GroupChipEmptyText>}
            {groups.map(group => (
              <GroupChip key={group.id}>
                {group.name}
                <GroupChipRemove
                  title={t('이 그룹에서 제외')}
                  onClick={() => !removeMutation.isPending && removeMutation.mutate(group.id)}>
                  <CloseIcon sx={{ fontSize: 13 }} />
                </GroupChipRemove>
              </GroupChip>
            ))}
            <GroupChipAddButton onClick={e => setAddAnchor(e.currentTarget)}>
              <AddIcon sx={{ fontSize: 14 }} />
              {t('그룹')}
            </GroupChipAddButton>
          </>
        )}
      </GroupChipList>

      <GroupAddPopover
        open={!!addAnchor}
        anchorEl={addAnchor}
        onClose={() => setAddAnchor(null)}
        selectedMembers={[{ memberId, name: memberName }]}
        branchId={branchId}
      />
    </GroupChipRow>
  );
};

export default MemberGroupChips;
